// swr-warm.js — the SWR warm-target registry + background-warming driver.
//
// "Cache so revisits are instant" needs two things per board: (1) the screen
// paints its DEFAULT board from the SWR cache on entry, and (2) after the first
// board paints, the OTHER boards are fetched-and-cached in the background so the
// NEXT navigation is already a warm hit. Both need the SAME (cacheKey, default
// loader) pair, so it lives here once and is imported by bridge.js (warming) and
// the Tasks/Messaging screens (their instant-paint entry load).
//
// Only DEFAULT (unfiltered) board queries are cached — the query a user lands on
// before touching a filter chip. Filter-narrowed reads (mine/urgent/one-status,
// a single thread's scope) go straight to Medplum: they're cheap, transient, and
// caching every filter permutation would bloat localStorage for near-zero payoff.
//
// The consult ('encounter') route is DELIBERATELY ABSENT here and must stay so —
// an actively-edited clinical chart must never paint stale, and must never be
// warmed behind the user's back (AGENTS.md §2, clinical freshness). Anonymous
// surfaces (kiosk/intake) are absent too — they never ride the logged-in session.
import { swrLoad } from './medplum-live.js';
import { loadQueueFromMedplum } from './medplum-queue.js';
import { loadReceptionFromMedplum } from './medplum-reception.js';
import { loadPatientsFromMedplum } from './medplum-patients.js';
import { loadScheduleFromMedplum } from './medplum-schedule.js';
import { loadReportsFromMedplum } from './medplum-reports.js';
import { loadTasksFromMedplum } from './medplum-tasks.js';
import { listThreadsFromMedplum } from './medplum-messaging.js';

// cacheKey → the DEFAULT-query loader for that board. The cacheKey is what
// swrLoad folds into the per-user localStorage key; it MUST match the key the
// screen uses for its own instant-paint load, or warming and the screen would
// write to different slots. Tasks' default board = open tasks, all scope;
// Messaging's default = all threads.
export const WARM_TARGETS = {
  queue: () => loadQueueFromMedplum({}),
  reception: () => loadReceptionFromMedplum({}),
  patients: () => loadPatientsFromMedplum({}),
  schedule: () => loadScheduleFromMedplum({}),
  reports: () => loadReportsFromMedplum({}),
  tasks: () => loadTasksFromMedplum({ open: true }),
  messaging: () => listThreadsFromMedplum({ mine: null }),
};

/** True when we should NOT spend bandwidth warming (data-saver on, or offline). */
function warmingSuppressed() {
  try {
    if (typeof navigator !== 'undefined') {
      if (navigator.onLine === false) return true;
      if (navigator.connection && navigator.connection.saveData) return true;
    }
  } catch (_) { /* no navigator/connection → don't suppress */ }
  return false;
}

/**
 * After the active board has painted, fire-and-forget an SWR read for every
 * OTHER warm target so the next navigation is a warm cache hit. Never throws
 * (each warm is independently caught); returns immediately.
 *
 * @param {string} activeCacheKey  the board the user just landed on — skipped
 *        (it already loaded through its own SWR path).
 */
export function warmSwrBoards(activeCacheKey) {
  if (warmingSuppressed()) return;
  for (const [key, loader] of Object.entries(WARM_TARGETS)) {
    if (key === activeCacheKey) continue;
    // swrLoad writes the cache on both hit and miss; onRevalidate omitted so a
    // warm read never repaints a screen the user isn't looking at.
    Promise.resolve()
      .then(() => swrLoad(key, loader))
      .catch(() => { /* a board that can't warm just isn't warm — no surface */ });
  }
}
